import React, { useEffect, useState } from "react";
import { FiCheckSquare, FiX } from "react-icons/fi";
import { AnimatePresence, motion } from "framer-motion";
import { getConsumer } from "@/utils/cable"
import { useGlobalState } from "@/state"

const NOTIFICATION_TTL = 30000;

const PopupAlerts = () => {
  const [toasts, setToasts] = useState([]);
  const [currentUser, setCurrentUser] = useGlobalState('currentUser')
  const [, setLiveReleaseInfo] = useGlobalState('liveReleaseInfo')
  const [, setNotificationCounts] = useGlobalState('notificationCounts')
  const removeToast = (id) => {
    setToasts((pv) => pv.filter((n) => n.id !== id));
  };

  const addToast = (data) => {
    // add toast only if it doesn't already exist
    if (toasts.find((n) => n.id === data.id)) return;

    //console.log('adding toast', data.id, toasts)

    setToasts((pv) => [...pv, {...data}]);
    const timeoutRef = setTimeout(() => {
      removeToast(data.id);
    }, NOTIFICATION_TTL);
  };

  useEffect(() => {
    // No user, no socket. The connection is rejected anyway
    // (reject_unauthorized_connection), and the wiki layout mounts this header
    // logged-out, so that was a handshake per anonymous wiki page view.
    if (!currentUser?.id) { return }

    const subscription = getConsumer().subscriptions.create("NotificationsChannel", {
      received(data) {
        // Sent by the channel on every subscribe, reconnects included. Carries
        // the badge counts and the release that's actually running, which is
        // the whole job the five-minute heartbeat poll used to do.
        if (data.type === 'bootstrap') {
          setLiveReleaseInfo(data.releaseInfo)
          setNotificationCounts(data.notificationCounts)
          return
        }

        // The messages page shows new DMs live, so a toast there is a second
        // notice of something already on screen. In the thread itself the
        // message is read the moment it lands, so the badge stays put too.
        // A DM mention's toast is dropped in its own thread too, where the
        // message is already on screen; its bell notification still counts.
        // "Incoming Message" is only dropped where the inbox is on screen to
        // show it: the inbox page, or any messages page from md up. On a phone
        // reading another thread there's no inbox, so the toast is the notice.
        const path = window.location.pathname
        const inboxVisible = path === '/private_messages' || (path.startsWith('/private_messages/') && window.matchMedia('(min-width: 768px)').matches)
        const pmPath = data.data?.id === 'new_private_message' && inboxVisible
        const onItsThread = !!data.data?.postId && window.location.pathname === `/private_messages/${data.data.postId}`
        if (pmPath && onItsThread) { return }

        if (data.data?.id && !pmPath && !onItsThread) { addToast(data.data) }
        // The bell badge only knew about alerts from page load. Light it up now.
        if (data.data?.type === 'alert') {
          setCurrentUser((u) => (u ? { ...u, notificationsCount: (u.notificationsCount || 0) + 1 } : u))
        }
        // Same trick for the Messages badge. Counting server-side would mean a
        // query per push and a fan-out cost per recipient; +1 here costs
        // nothing, and the next bootstrap corrects any drift.
        if (data.data?.id === 'new_private_message') {
          setNotificationCounts((c) => ({ ...c, messages: (c.messages || 0) + 1 }))
        }
      }
    });

    // The consumer is shared with the rest of the page, so only this
    // subscription goes.
    return () => { subscription.unsubscribe() }
  }, [currentUser?.id]);

  return <div className="flex flex-col gap-1 w-72 fixed bottom-5 left-3 z-50 pointer-events-none">
    <AnimatePresence>
      { toasts.map((n) => (
        <Popup removeNotif={removeToast} {...n} key={n.id} />
      ))}
    </AnimatePresence>
  </div>
}

const Popup = ({ message, type, title, image_url, href, id, removeNotif }) => {
  useEffect(() => {
    const timeoutRef = setTimeout(() => {
      removeNotif(id);
    }, NOTIFICATION_TTL);

    return () => clearTimeout(timeoutRef);
  }, []);

  return (
    <motion.div
      layout
      initial={{ y: -15, scale: 0.95 }}
      animate={{ y: 0, scale: 1 }}
      exit={{ x: "100%", opacity: 0 }}
      transition={{ duration: 0.35, ease: "easeOut" }}
      className={`p-2 flex items-start rounded font-medium shadow-lg text-white bg-cccblue pointer-events-auto border-2 border-black`}
    >
      { type === 'profile_view' && <a href={href} target="_blank" className="w-full flex">
        <div className="overflow-hidden rounded-sm w-12 h-12">
          <img src={image_url} alt="" />
        </div>
        <div className="pl-3 flex flex-col justify-between">
          <h6 className="font-semibold text-white">{title}</h6>
          <span className='text-white text-sm'>{message}</span>
        </div>
      </a>
      }
      { type === 'alert' && <a href={href} target="_blank" className="w-full flex">
        <div className="pl-3 flex flex-col justify-between">
          <h6 className="font-semibold text-white">{title}</h6>
          <span className='text-white text-sm'>{message}</span>
        </div>
      </a>
      }
      <button onClick={() => removeNotif(id)} className="ml-auto mt-0.5">
        <FiX />
      </button>
    </motion.div>
  );
};

export default PopupAlerts;
